import CheckIcon from "@mui/icons-material/Check";
import { Alert, Box, Button } from "@mui/material";
import { FormEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import {
  CURRENT_PASSWORD_WRONG,
  MIN_PASSWORD_LENGTH,
} from "../../shared/passwordChange";
import { DialogShell } from "../general/DialogShell";
import { useAuth } from "./ParseAuthContext";
import { canChangePassword, missingCharacters } from "./passwordEntries";
import { PasswordField } from "./PasswordField";

const KEY_PREFIX = "appBar.userProfile.security.password.dialog";

/**
 * Asks for the current password and the new one twice, and changes the
 * password of the user who is signed in. It is drawn only while it is open,
 * so what was typed is gone once it closes.
 */
export const ChangePasswordDialog = ({
  onClose,
  onChanged,
}: {
  onClose: () => void;
  onChanged: () => void;
}) => {
  const { theme } = useTheme();
  const { changePassword } = useAuth();
  const { t } = useTranslation(undefined, { keyPrefix: KEY_PREFIX });
  const formId = useId();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  // a repetition is compared once it is typed, not letter by letter
  const [repeatLeft, setRepeatLeft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // what the server refused: the current password, or the change for another
  // reason
  const [refused, setRefused] = useState<"current" | "change">();

  const missing = missingCharacters(next);
  const rule =
    next === "" ? (
      t("rule", { min: MIN_PASSWORD_LENGTH })
    ) : missing > 0 ? (
      t("ruleMissing", { min: MIN_PASSWORD_LENGTH, missing })
    ) : (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontWeight: 600,
          color: theme.palette.data.positive.dark,
        }}
      >
        <CheckIcon sx={{ fontSize: 14 }} />
        {t("rule", { min: MIN_PASSWORD_LENGTH })}
      </Box>
    );

  const close = () => {
    if (!submitting) onClose();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting || !canChangePassword({ current, next, repeat })) return;
    setSubmitting(true);
    setRefused(undefined);
    try {
      await changePassword(current, next);
      onChanged();
    } catch (error) {
      console.warn("Failed to change the password", error);
      setRefused(
        (error as { code?: number })?.code === CURRENT_PASSWORD_WRONG
          ? "current"
          : "change",
      );
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      open
      onClose={close}
      title={t("title")}
      description={t("description")}
      actions={
        <>
          <Button onClick={close} color="inherit" disabled={submitting}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="contained"
            disabled={!canChangePassword({ current, next, repeat })}
            loading={submitting}
          >
            {t("confirm")}
          </Button>
        </>
      }
    >
      <Box
        component="form"
        id={formId}
        onSubmit={submit}
        noValidate
        sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
      >
        <PasswordField
          label={t("current")}
          value={current}
          onChange={(value) => {
            setCurrent(value);
            if (refused === "current") setRefused(undefined);
          }}
          autoComplete="current-password"
          autoFocus
          disabled={submitting}
          error={refused === "current" ? t("currentWrong") : undefined}
          size="small"
        />
        <PasswordField
          label={t("new")}
          value={next}
          onChange={setNext}
          autoComplete="new-password"
          disabled={submitting}
          hint={rule}
          size="small"
        />
        <PasswordField
          label={t("repeat")}
          value={repeat}
          onChange={setRepeat}
          onBlur={() => setRepeatLeft(true)}
          autoComplete="new-password"
          disabled={submitting}
          error={
            repeatLeft && repeat !== "" && repeat !== next
              ? t("mismatch")
              : undefined
          }
          size="small"
        />
        {refused === "change" && <Alert severity="error">{t("failed")}</Alert>}
        <Alert severity="info">{t("note")}</Alert>
      </Box>
    </DialogShell>
  );
};
